import { createSelector } from 'reselect'
import { pendingSelector } from 'api/selectors'
import * as auth from '../auth'
import { creditCardsSelector } from 'credit-cards/selectors'
import { vehiclesSelector } from 'dashboard-vehicles/selectors'

export const isOnDemandParkingActivatedSelector = createSelector(auth.selectors.userSelector, user =>
    Boolean(user?.onDemandOptIn)
)

const stateSelector = state => state.onDemandParking
const baselineSelector = createSelector(stateSelector, s => s?.baseline)
const draftSelector = createSelector(stateSelector, s => s?.draft)

export const baselineEnabledSelector = createSelector(baselineSelector, b => b?.enabled ?? false)
export const baselineSelectedVehicleIdsSelector = createSelector(baselineSelector, b => b?.selectedVehicleIds || [])
export const baselineSelectedCardIdSelector = createSelector(baselineSelector, b => b?.selectedCardId ?? null)

export const draftEnabledSelector = createSelector(draftSelector, d => d?.enabled ?? false)
export const draftSelectedVehicleIdsSelector = createSelector(draftSelector, d => d?.selectedVehicleIds || [])
export const draftSelectedCardIdSelector = createSelector(draftSelector, d => d?.selectedCardId ?? null)

export const hasAnySelectedVehicleSelector = createSelector(
    draftSelectedVehicleIdsSelector,
    selectedVehicleIds => selectedVehicleIds.length > 0
)

export const hasSelectedCardSelector = createSelector(
    draftSelectedCardIdSelector,
    selectedCardId => selectedCardId !== null
)

const areSameIdSets = (a = [], b = []) => {
    if (a.length !== b.length) return false
    const bSet = new Set(b)
    return a.every(id => bSet.has(id))
}

export const isDirtySelector = createSelector(
    baselineEnabledSelector,
    baselineSelectedVehicleIdsSelector,
    baselineSelectedCardIdSelector,
    draftEnabledSelector,
    draftSelectedVehicleIdsSelector,
    draftSelectedCardIdSelector,
    (baselineEnabled, baselineVehicles, baselineCardId, draftEnabled, draftVehicles, draftCardId) =>
        baselineEnabled !== draftEnabled ||
        !areSameIdSets(baselineVehicles, draftVehicles) ||
        baselineCardId !== draftCardId
)

export const canActivateSelector = createSelector(
    hasAnySelectedVehicleSelector,
    hasSelectedCardSelector,
    (hasVehicle, hasCard) => hasVehicle && hasCard
)

export const canSaveSelector = createSelector(
    isDirtySelector,
    hasAnySelectedVehicleSelector,
    hasSelectedCardSelector,
    (isDirty, hasVehicle, hasCard) => isDirty && hasVehicle && hasCard
)

export const onDemandActivatePendingSelector = createSelector(
    pendingSelector,
    pending => pending.activateOnDemandParking
)
export const onDemandSavePendingSelector = createSelector(pendingSelector, pending => pending.saveOnDemandParking)

export const onDemandParkingErrorSelector = createSelector(
    onDemandActivatePendingSelector,
    onDemandSavePendingSelector,
    (activatePending, savePending) => {
        if (activatePending instanceof Error) return activatePending
        if (savePending instanceof Error) return savePending
        return null
    }
)

export const onDemandCreditCardsSelector = creditCardsSelector

export const onDemandVehiclesSelector = vehiclesSelector
